import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { dataCache, CACHE_DURATIONS } from '../utils/dataCache';
import { isCloudflare503Error, isNetworkError } from '../utils/connectionUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { useAdFreeSubscriber } from '../contexts/AdFreeSubscriberContext';
import { languageContent } from '../utils/languageContent';
import { getSuperAdminStatus } from '../utils/adminUtils';
import { getAdUnlockedLessonIds } from '../api/adUnlocksApi';
import BottomNav from '../components/BottomNav';
import SkeletonLoader from '../components/SkeletonLoader';
import WatchAdUnlockModal from '../components/WatchAdUnlockModal';
import LessonBannerAd from '../components/LessonBannerAd';

interface Lesson {
  id: string;
  title: string;
  content: string;
  order_index: number;
}

interface Chapter {
  id: string;
  name: string;
  description: string;
}

const LessonsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { chapterId } = useParams<{ chapterId: string }>();
  const { currentLanguage } = useLanguage();
  const { isPremiumSubscriber } = useAdFreeSubscriber();
  const content = languageContent[currentLanguage];
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [, setChapter] = useState<Chapter | null>(null);
  const [loading, setLoading] = useState(false); // Start false - show cached data immediately
  const [completedLessons, setCompletedLessons] = useState<string[]>([]);
  const [adUnlockedLessons, setAdUnlockedLessons] = useState<string[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adModalLesson, setAdModalLesson] = useState<Lesson | null>(null);

  useEffect(() => {
    if (chapterId) {
      initializeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  const initializeData = async () => {
    // Load progress first (localStorage - instant)
    loadProgress();
    
    // Load cached data immediately (instant display)
    const superAdmin = await getSuperAdminStatus();
    setIsSuperAdmin(superAdmin);
    
    const cacheKey = `lessons_${chapterId}_${superAdmin ? 'admin' : 'user'}`;
    const cachedLessons = dataCache.get<Lesson[]>(cacheKey);
    const cachedChapter = dataCache.get<Chapter>(`chapter_${chapterId}`);
    
    if (cachedLessons && cachedLessons.length > 0) {
      setLessons(cachedLessons);
      setLoading(false);
    }
    
    if (cachedChapter) {
      setChapter(cachedChapter);
    }
    
    // Load fresh data in background (non-blocking)
    setTimeout(() => {
    loadChapter();
      loadLessons(superAdmin, false);
    }, 50);
  };

  const loadChapter = async () => {
    try {
      const cacheKey = `chapter_${chapterId}`;
      const cachedChapter = dataCache.get<Chapter>(cacheKey);
      
      if (cachedChapter) {
        setChapter(cachedChapter);
      }
      
      const { data, error } = await supabase
        .from('Chapters')
        .select('*')
        .eq('id', chapterId)
        .single();

      if (error) {
        // Check for Cloudflare 503
        if (isCloudflare503Error(error)) {
          console.warn('⚠️ Cloudflare is down (503). Using cached chapter.');
        } else if (isNetworkError(error)) {
          console.warn('⚠️ Network error. Using cached chapter.');
        }
        // Keep cached data on error
        if (cachedChapter) return;
        throw error;
      }
      
      setChapter(data);
      // Cache the chapter
      if (data) {
        dataCache.set(cacheKey, data, CACHE_DURATIONS.LEVELS);
      }
    } catch (error) {
      console.error('Error loading chapter:', error);
    }
  };

  const loadLessons = async (superAdminStatus?: boolean, forceRefresh: boolean = false) => {
    try {
      // Only show loading if we don't have cached data
      if (!forceRefresh && lessons.length === 0) {
        setLoading(true);
      }
      
      const isSuperAdminUser = superAdminStatus !== undefined ? superAdminStatus : isSuperAdmin;
      const cacheKey = `lessons_${chapterId}_${isSuperAdminUser ? 'admin' : 'user'}`;
      
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedLessons = dataCache.get<Lesson[]>(cacheKey);
        if (cachedLessons && cachedLessons.length > 0) {
          setLessons(cachedLessons);
          setLoading(false);
          // Refresh in background (non-blocking)
          setTimeout(() => loadLessons(superAdminStatus, true), 100);
          return;
        }
      }
      
      // Superadmins can see all lessons (including drafts), regular users only published
      let query = supabase
        .from('Lessons')
        .select('*')
        .eq('chapter_id', chapterId);
      
      // Only filter by status for non-superadmins
      if (!isSuperAdminUser) {
        query = query.eq('status', 'published');
      }
      
      const { data, error } = await query.order('order_index');

      if (error) {
        // Check for Cloudflare 503
        if (isCloudflare503Error(error)) {
          console.warn('⚠️ Cloudflare is down (503). Keeping existing lessons.');
        } else if (isNetworkError(error)) {
          console.warn('⚠️ Network error. Keeping existing lessons.');
        }
        
        // Keep existing data on error
        if (lessons.length > 0) {
          setLoading(false);
          return;
        }
        throw error;
      }
      
      const lessonsData = data || [];
      
      // If refresh returns empty and we have existing data, preserve existing data
      if (lessonsData.length === 0 && lessons.length > 0) {
        console.warn('No lessons data returned from refresh, keeping existing data');
        setLoading(false);
        return;
      }
      
      // Smooth update - only update if data actually changed
      setLessons(prevLessons => {
        // If data is the same, don't update to prevent flicker
        if (prevLessons.length === lessonsData.length && 
            prevLessons.every((prev, idx) => prev.id === lessonsData[idx]?.id)) {
          return prevLessons; // Keep existing to prevent re-render
        }
        return lessonsData;
      });
      
      // Cache the data (only if we have data)
      if (lessonsData.length > 0) {
        dataCache.set(cacheKey, lessonsData, CACHE_DURATIONS.LESSONS);
      }
    } catch (error) {
      console.error('Error loading lessons:', error);
      // On error, keep existing data visible
      if (lessons.length === 0) {
        setLessons([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = () => {
    try {
      const progressKey = 'userProgress';
      const existingProgress = localStorage.getItem(progressKey);
      const progress = existingProgress ? JSON.parse(existingProgress) : {
        completedLessons: []
      };
      setCompletedLessons(progress.completedLessons || []);
      setAdUnlockedLessons(getAdUnlockedLessonIds());
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const handleLessonSelect = (lesson: Lesson, isLocked: boolean) => {
    if (!isLocked) {
      navigate(`/lesson/${lesson.id}`);
    } else {
      setAdModalLesson(lesson);
    }
  };

  const handleAdUnlocked = useCallback((lessonId: string) => {
    setAdUnlockedLessons(prev => [...prev, lessonId]);
    setAdModalLesson(null);
    navigate(`/lesson/${lessonId}`);
  }, [navigate]);

  const handleCloseAdModal = useCallback(() => {
    setAdModalLesson(null);
  }, []);

  return (
    <div className="lessons-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content.lesson}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      <div className="lessons-container">
        {loading && lessons.length === 0 ? (
          <div className="no-lessons">
            <SkeletonLoader type="card" count={3} />
          </div>
        ) : lessons.length === 0 ? (
          <div className="no-lessons">
            <p>No lessons available yet.</p>
          </div>
        ) : (
          lessons.map((lesson, index) => {
            const isCompleted = completedLessons.includes(lesson.id);
            const isAdUnlocked = adUnlockedLessons.includes(lesson.id);
            // Superadmins, admin-approved ad-free subscribers, and ad-unlocked lessons bypass the sequential lock
            const isLocked =
              !isSuperAdmin &&
              !isPremiumSubscriber &&
              !isAdUnlocked &&
              index > 0 &&
              !completedLessons.includes(lessons[index - 1].id);
            
            // Define standard modern app colors
            const colors = ['#ff6b35', '#4caf50', '#9c27b0'];
            const color = colors[index % colors.length];
            
            // Get lesson number
            const lessonNumber = index + 1;

            return (
              <div 
                key={lesson.id}
                className={`lesson-card ${isLocked ? 'locked' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => handleLessonSelect(lesson, isLocked)}
              >
                <div className="wrap-lesson-icon">
                  <div className="lesson-icon">
                    <div className="icon-placeholder">{lessonNumber}</div>
                  </div>
                  <div className="lesson-content">
                    <div className="lesson-label">Lesson</div>
                    <h3 className="lesson-title">{lesson.title}</h3>
                    
                    <div className="lesson-status-section">
                      {isCompleted && <div className="status-badge completed">✓ Completed</div>}
                      {isLocked && <div className="status-badge locked">🔒 Watch ad to unlock</div>}
                      {!isCompleted && !isLocked && <div className="status-badge available">Start</div>}
                    </div>
                  </div>
                </div>

                {isLocked ? (
                  <div className="lesson-ad-unlock-hint">🎬</div>
                ) : (
                  <div className="lesson-arrow">→</div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Watch Ad to Unlock Modal */}
      {adModalLesson && (
        <WatchAdUnlockModal
          lessonId={adModalLesson.id}
          lessonTitle={adModalLesson.title}
          onUnlocked={() => handleAdUnlocked(adModalLesson.id)}
          onClose={handleCloseAdModal}
        />
      )}
    </div>
  );
};

export default LessonsScreen;

